/**
 * GIS Map Controller Manager
 * Centralized manager for all map operations using OpenStreetMap controller
 */

import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { MapOperationsOptimizedService } from '../services/map-operations.optimized.service';
import { GisMonitoringPoint, GisPolygon, GisMapEvent } from '@components/openstreetmap/interfaces/gis-component.interfaces';

@Injectable({
  providedIn: 'root'
})
export class GisMapControllerManager {
  private mapOpsService = inject(MapOperationsOptimizedService);

  // =========================================================================
  // MARKER MANAGEMENT
  // =========================================================================

  /**
   * Add a monitoring point to the map
   */
  addMonitoringPoint(point: GisMonitoringPoint): void {
    this.mapOpsService.addMarker({
      id: point.id,
      lat: point.lat,
      lng: point.lng,
      title: point.title || point.name,
      icon: 'map-marker-alt',
      color: '#3388ff',
      popup: `
        <div class="monitoring-point-popup">
          <strong>${point.name}</strong>
          <p>Lat: ${point.lat.toFixed(4)}, Lng: ${point.lng.toFixed(4)}</p>
        </div>
      `
    });
  }

  /**
   * Add multiple monitoring points
   */
  addMonitoringPoints(points: GisMonitoringPoint[]): void {
    const markers = points.map(point => ({
      id: point.id,
      lat: point.lat,
      lng: point.lng,
      title: point.title || point.name,
      icon: 'map-marker-alt',
      color: '#3388ff'
    }));
    this.mapOpsService.addMarkers(markers);
  }

  /**
   * Remove monitoring point
   */
  removeMonitoringPoint(pointId: string): void {
    this.mapOpsService.removeMarker(pointId);
  }

  /**
   * Highlight monitoring point
   */
  highlightMonitoringPoint(pointId: string, highlight: boolean): void {
    this.mapOpsService.updateMarkerStyle(pointId, {
      color: highlight ? '#ff6b6b' : '#3388ff'
    });
  }

  /**
   * Zoom to monitoring point
   */
  zoomToMonitoringPoint(point: GisMonitoringPoint, zoomLevel: number = 12): void {
    this.mapOpsService.setMapCenter(point.lat, point.lng, zoomLevel);
  }

  // =========================================================================
  // POLYGON MANAGEMENT
  // =========================================================================

  /**
   * Add polygon to map
   */
  addPolygonShape(polygon: GisPolygon): void {
    this.mapOpsService.addPolygon({
      id: polygon.id,
      coordinates: polygon.coordinates,
      name: polygon.name,
      fillColor: polygon.fillColor || '#3388ff',
      strokeColor: polygon.strokeColor || '#000000',
      fillOpacity: polygon.fillOpacity ?? 0.5,
      strokeWidth: polygon.strokeWidth ?? 2,
      popup: polygon.name
    });
  }

  /**
   * Add multiple polygons
   */
  addPolygonShapes(polygons: GisPolygon[]): void {
    const polygonsList = polygons.map(poly => ({
      id: poly.id,
      coordinates: poly.coordinates,
      name: poly.name,
      fillColor: poly.fillColor || '#3388ff',
      strokeColor: poly.strokeColor || '#000000',
      fillOpacity: poly.fillOpacity ?? 0.5,
      strokeWidth: poly.strokeWidth ?? 2
    }));
    this.mapOpsService.addPolygons(polygonsList);
  }

  /**
   * Remove polygon from map
   */
  removePolygonShape(polygonId: string): void {
    this.mapOpsService.removePolygon(polygonId);
  }

  /**
   * Clear polygons by type
   */
  clearPolygonsOfType(type: 'region' | 'field' | 'season' | 'monitoring'): void {
    this.mapOpsService.clearPolygonsByType(type);
  }

  /**
   * Highlight polygon
   */
  highlightPolygon(polygonId: string, highlight: boolean): void {
    this.mapOpsService.updatePolygonStyle(polygonId, {
      fillOpacity: highlight ? 0.7 : 0.5,
      strokeWidth: highlight ? 3 : 2
    });
  }

  /**
   * Fit map bounds to polygon
   */
  fitToPolygon(polygon: GisPolygon, padding: number = 50): void {
    const bounds = this.mapOpsService.calculateBoundsFromPoints(polygon.coordinates);
    this.mapOpsService.fitBounds(bounds, padding);
  }

  /**
   * Fit map bounds to all polygons
   */
  fitToAllPolygons(polygons: GisPolygon[], padding: number = 50): void {
    const allCoords = polygons.flatMap(p => p.coordinates);
    if (allCoords.length > 0) {
      const bounds = this.mapOpsService.calculateBoundsFromPoints(allCoords);
      this.mapOpsService.fitBounds(bounds, padding);
    }
  }

  /**
   * Update polygon style
   */
  updatePolygonAppearance(
    polygonId: string,
    fillColor?: string,
    fillOpacity?: number,
    strokeColor?: string
  ): void {
    this.mapOpsService.updatePolygonStyle(polygonId, {
      fillColor: fillColor,
      fillOpacity: fillOpacity,
      strokeColor: strokeColor
    });
  }

  // =========================================================================
  // DRAWING OPERATIONS
  // =========================================================================

  /**
   * Enable polygon drawing mode
   */
  enablePolygonDrawing(): void {
    this.mapOpsService.enableDrawing('polygon', {
      color: '#3388ff',
      fillOpacity: 0.5
    });
  }

  /**
   * Enable point drawing mode
   */
  enablePointDrawing(): void {
    this.mapOpsService.enableDrawing('point');
  }

  /**
   * Disable drawing mode
   */
  disableDrawing(): void {
    this.mapOpsService.disableDrawing();
  }

  /**
   * Check if drawing is enabled
   */
  isDrawing(): boolean {
    return this.mapOpsService.isDrawingEnabled();
  }

  // =========================================================================
  // VIEW OPERATIONS
  // =========================================================================

  /**
   * Set map center
   */
  setCenter(lat: number, lng: number, zoom?: number): void {
    this.mapOpsService.setMapCenter(lat, lng, zoom);
  }

  /**
   * Get current map center
   */
  getCenter(): { lat: number; lng: number } {
    return this.mapOpsService.getMapCenter();
  }

  /**
   * Set zoom level
   */
  setZoom(zoomLevel: number): void {
    this.mapOpsService.setZoom(zoomLevel);
  }

  /**
   * Fly to location with animation
   */
  flyTo(lat: number, lng: number, zoom?: number): void {
    this.mapOpsService.flyTo(lat, lng, zoom, 1000);
  }

  /**
   * Pan to location without animation
   */
  panTo(lat: number, lng: number): void {
    this.mapOpsService.panTo(lat, lng);
  }

  /**
   * Reset map to default view
   */
  resetView(lat?: number, lng?: number, zoom?: number): void {
    this.mapOpsService.resetMap(lat, lng, zoom);
  }

  /**
   * Fit bounds to show all markers and polygons
   */
  fitToAllShapes(padding: number = 50): void {
    const markers = this.mapOpsService.getMarkers();
    const polygons = this.mapOpsService.getPolygons();

    const allPoints: Array<[number, number]> = [];

    markers.forEach(m => allPoints.push([m.lat, m.lng]));
    polygons.forEach(p => allPoints.push(...p.coordinates));

    if (allPoints.length > 0) {
      const bounds = this.mapOpsService.calculateBoundsFromPoints(allPoints);
      this.mapOpsService.fitBounds(bounds, padding);
    }
  }

  // =========================================================================
  // LAYER OPERATIONS
  // =========================================================================

  /**
   * Set base map layer
   */
  setBaseMap(mapType: 'osm' | 'satellite' | 'terrain'): void {
    // Map common names to actual base map types
    const mappedType: 'osm' | 'landsat' | 'sentinel' =
      mapType === 'satellite' ? 'landsat' :
      mapType === 'terrain' ? 'sentinel' :
      'osm';

    this.mapOpsService.setBaseMap(mappedType);
  }

  /**
   * Set layer visibility
   */
  setLayerVisibility(layerName: string, visible: boolean): void {
    this.mapOpsService.setLayerVisibility(layerName, visible);
  }

  /**
   * Set layer opacity
   */
  setLayerOpacity(layerName: string, opacity: number): void {
    this.mapOpsService.setLayerOpacity(layerName, opacity);
  }

  // =========================================================================
  // UTILITY METHODS
  // =========================================================================

  /**
   * Get all markers
   */
  getAllMarkers(): any[] {
    return this.mapOpsService.getMarkers();
  }

  /**
   * Get all polygons
   */
  getAllPolygons(): any[] {
    return this.mapOpsService.getPolygons();
  }

  /**
   * Calculate distance between two points (in km)
   */
  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    return this.mapOpsService.calculateDistance(
      { lat: lat1, lng: lng1 },
      { lat: lat2, lng: lng2 }
    );
  }

  /**
   * Export map to GeoJSON
   */
  exportToGeoJSON(): any {
    return this.mapOpsService.exportToGeoJSON();
  }

  /**
   * Import from GeoJSON
   */
  importFromGeoJSON(geoJSON: any): void {
    this.mapOpsService.importFromGeoJSON(geoJSON);
  }

  /**
   * Show popup at coordinates
   */
  showPopup(lat: number, lng: number, content: string): void {
    this.mapOpsService.showPopup(lat, lng, content);
  }

  /**
   * Close all popups
   */
  closePopups(): void {
    this.mapOpsService.closePopups();
  }

  /**
   * Clear all map content
   */
  clearAll(): void {
    this.mapOpsService.clearMarkers();
    this.mapOpsService.clearPolygons();
    this.mapOpsService.disableDrawing();
  }
}
